import approvalsJson from "./approvals.json";
import capturesJson from "./captures.json";
import { PRODUCT_IMAGE_CATALOGUE, productImageEntry } from "./catalogue";
import type {
  ApprovedProductImage,
  ProductImageCapture,
  ProductImageLocale,
  ResolvedProductImage,
} from "./types";

export * from "./types";
export { PRODUCT_IMAGE_CATALOGUE, productImageEntry };

/** Written by `yarn product-images:approve`; the only record the website reads. */
export const PRODUCT_IMAGE_APPROVALS = approvalsJson as unknown as Record<
  string,
  ApprovedProductImage
>;
/** Written by the capture run; candidates awaiting review. */
export const PRODUCT_IMAGE_CAPTURES = capturesJson as unknown as Record<
  string,
  ProductImageCapture
>;

/**
 * The approved image for an entry, or `null` when the owner has not approved
 * one. A newer capture never shows here until it is approved.
 */
export function resolveApprovedProductImage(
  id: string,
  locale: ProductImageLocale,
  approvals: Record<string, ApprovedProductImage>,
  width?: number,
): ResolvedProductImage | null {
  const entry = productImageEntry(id);
  const approved = approvals[id];
  if (!approved) return null;
  const rendition =
    width && width >= 1440 ? approved.renditions?.["1920"] : undefined;
  const presentation = approved.presentation ?? entry;
  return {
    entry: { ...entry, text: presentation.text, visual: presentation.visual },
    locale,
    file: rendition?.files[locale] ?? approved.files[locale],
    elements:
      rendition?.elementsByLocale[locale] ??
      approved.elementsByLocale?.[locale] ??
      approved.elements,
    metrics: approved.metrics[locale] ?? {},
    text: presentation.text[locale],
  };
}

export function getApprovedProductImage(
  id: string,
  locale: ProductImageLocale,
  width?: number,
): ResolvedProductImage | null {
  return resolveApprovedProductImage(
    id,
    locale,
    PRODUCT_IMAGE_APPROVALS,
    width,
  );
}

/** Ids whose latest capture differs from what the owner approved. */
export function pendingProductImages(
  captures: Record<string, ProductImageCapture> = PRODUCT_IMAGE_CAPTURES,
  approvals: Record<string, ApprovedProductImage> = PRODUCT_IMAGE_APPROVALS,
): string[] {
  return Object.entries(captures)
    .filter(
      ([id, capture]) => approvals[id]?.fingerprint !== capture.fingerprint,
    )
    .map(([id]) => id)
    .sort();
}
